import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { buscarAuditoria, type RegistroAuditoria } from '../lib/escalas';
import { Alerta, Botao, Cartao } from '../componentes/Interface';
import Layout from '../componentes/Layout';

const ACOES: Record<string, string> = {
  entrou: 'entrou',
  saiu: 'saiu',
  login_falhou: 'login falhou',
  criou: 'criou',
  editou: 'editou',
  excluiu: 'excluiu',
  trocou_senha: 'trocou a senha',
  definiu_senha: 'definiu senha',
  revogou_token: 'gerou novo link',
  gerou_token: 'pediu recuperacao',
};

const CORES: Record<string, string> = {
  criou: 'bg-tinta-900 text-white',
  editou: 'bg-amber-100 text-amber-800',
  excluiu: 'bg-red-100 text-red-700',
  login_falhou: 'bg-red-100 text-red-700',
};

export default function Logs() {
  const [registros, setRegistros] = useState<RegistroAuditoria[]>([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [entidade, setEntidade] = useState('');
  const [acao, setAcao] = useState('');
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const d = await buscarAuditoria(pagina, { entidade, acao });
      setRegistros(d.registros);
      setTotal(d.total);
      setErro(null);
    } catch (e) {
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o log.');
    }
  }, [pagina, entidade, acao]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  const paginas = Math.max(1, Math.ceil(total / 50));

  return (
    <Layout titulo="Logs" subtitulo="Quem fez o que, e quando. Inclusive edicao e exclusao.">
      {erro && (
        <div className="mb-4 max-w-2xl">
          <Alerta>{erro}</Alerta>
        </div>
      )}

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          value={entidade}
          onChange={(e) => {
            setPagina(1);
            setEntidade(e.target.value);
          }}
          className="rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
        >
          <option value="">tudo</option>
          {['card', 'evento', 'escala', 'usuario', 'membro', 'coluna', 'funcao', 'arquivo', 'agenda'].map(
            (e) => (
              <option key={e} value={e}>
                {e}
              </option>
            ),
          )}
        </select>
        <select
          value={acao}
          onChange={(e) => {
            setPagina(1);
            setAcao(e.target.value);
          }}
          className="rounded-xl border border-tinta-200 bg-white px-3 py-2 text-sm"
        >
          <option value="">todas as ações</option>
          {Object.keys(ACOES).map((a) => (
            <option key={a} value={a}>
              {ACOES[a]}
            </option>
          ))}
        </select>
        <span className="self-center text-sm text-tinta-400">{total} registros</span>
      </div>

      <Cartao className="overflow-x-auto p-0">
        <table className="w-full min-w-[46rem] text-sm">
          <thead>
            <tr className="border-b border-tinta-200 text-left text-xs uppercase tracking-wide text-tinta-400">
              <th className="px-5 py-3">Quando</th>
              <th className="px-3 py-3">Quem</th>
              <th className="px-3 py-3">Ação</th>
              <th className="px-3 py-3">Onde</th>
              <th className="px-3 py-3">Detalhes</th>
            </tr>
          </thead>
          <tbody>
            {registros.map((r) => (
              <tr key={r.id} className="border-b border-tinta-100 align-top">
                <td className="whitespace-nowrap px-5 py-2.5 text-xs text-tinta-400">
                  {r.quando
                    ? new Intl.DateTimeFormat('pt-BR', {
                        timeZone: 'America/Sao_Paulo',
                        day: '2-digit',
                        month: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit',
                      }).format(new Date(r.quando))
                    : ''}
                </td>
                <td className="px-3 py-2.5 text-tinta-900">{r.quem}</td>
                <td className="px-3 py-2.5">
                  <span
                    className={`rounded-lg px-2 py-0.5 text-xs font-semibold ${
                      CORES[r.acao] ?? 'bg-tinta-100 text-tinta-600'
                    }`}
                  >
                    {ACOES[r.acao] ?? r.acao}
                  </span>
                </td>
                <td className="px-3 py-2.5 text-tinta-600">
                  {r.entidade}
                  {r.entidadeId ? ` #${r.entidadeId}` : ''}
                </td>
                <td className="max-w-[22rem] px-3 py-2.5 text-xs text-tinta-400">
                  <span className="line-clamp-2 break-words">{r.detalhes ?? '—'}</span>
                </td>
              </tr>
            ))}
            {registros.length === 0 && (
              <tr>
                <td colSpan={5} className="px-5 py-6 text-center text-sm text-tinta-400">
                  Nada registrado com esse filtro.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Cartao>

      {paginas > 1 && (
        <div className="mt-4 flex items-center gap-3">
          <Botao variante="secundario" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Anterior
          </Botao>
          <span className="text-sm text-tinta-400">
            pagina {pagina} de {paginas}
          </span>
          <Botao
            variante="secundario"
            disabled={pagina >= paginas}
            onClick={() => setPagina((p) => p + 1)}
          >
            Proxima
          </Botao>
        </div>
      )}
    </Layout>
  );
}
